export const ROLE_FAMILIES = {
  midwife: {
    parentRoleKey: 'midwife',
    variants: [
      {
        variantKey: 'community_midwife',
        title: 'Community Midwife',
        plainEnglishTitle: 'Community Midwife',
        aliases: ['community-based midwife', 'community midwifery team'],
        searchTerms: ['community midwife', 'community midwifery', 'community-based midwife'],
        journeyDisplay: 'variant_label',
        summary: 'Supports you during pregnancy and after birth, often through community clinics or visits at home.',
        differenceFromParent: 'A community midwife is a registered midwife who mainly provides care outside hospital. They may carry out pregnancy and postnatal checks, answer questions, discuss care plans and help arrange further support. Where and when you see them depends on your local maternity service.',
      },
      {
        variantKey: 'specialist_midwife',
        title: 'Specialist Midwife',
        plainEnglishTitle: 'Specialist Midwife',
        aliases: ['maternal medicine midwife', 'diabetes specialist midwife', 'research midwife', 'digital midwife', 'safeguarding midwife', 'fetal monitoring midwife'],
        searchTerms: ['specialist midwife', 'maternal medicine midwife', 'diabetes midwife', 'epilepsy midwife', 'fetal surveillance midwife', 'fetal medicine midwife', 'research midwife', 'digital midwife', 'safeguarding midwife', 'perinatal mental health midwife'],
        journeyDisplay: 'variant_label',
        summary: 'A registered midwife whose work focuses on a particular area of maternity care or a specialist service.',
        differenceFromParent: 'Some specialist midwives support care involving diabetes, epilepsy, heart conditions, maternal medicine, fetal medicine, fetal monitoring or perinatal mental health. Others work in research, digital maternity services, safeguarding or feeding support. Their responsibilities vary: not every specialist midwife provides appointments, and specialist care may also involve doctors and other professionals. Taking part in research is voluntary.',
      },
      {
        variantKey: 'labour_ward_coordinator',
        title: 'Labour Ward Coordinator',
        plainEnglishTitle: 'Labour Ward Coordinator',
        aliases: ['delivery suite coordinator', 'labour ward co-ordinator', 'coordinating midwife'],
        searchTerms: ['labour ward coordinator', 'labour ward co-ordinator', 'delivery suite coordinator', 'shift coordinator'],
        journeyDisplay: 'variant_label',
        summary: 'An experienced midwife who helps coordinate care across the labour ward.',
        differenceFromParent: 'The labour ward coordinator helps organise care on the labour ward, supports the midwifery team and helps arrange senior input when needed. They work with the wider maternity team; the midwife looking after you continues to be involved in your care.',
      },
      {
        variantKey: 'consultant_midwife',
        title: 'Consultant Midwife',
        plainEnglishTitle: 'Consultant Midwife',
        aliases: [],
        searchTerms: ['consultant midwife'],
        journeyDisplay: 'variant_label',
        summary: 'A very senior midwife with expert clinical, leadership and service-development responsibilities.',
        differenceFromParent: 'A consultant midwife is an experienced senior midwife with advanced expertise and leadership responsibilities. They may become involved in complex care, specialist advice, service improvement or professional leadership.',
      },
      {
        variantKey: 'student_midwife',
        title: 'Student Midwife',
        plainEnglishTitle: 'Student Midwife',
        aliases: ['midwifery student'],
        searchTerms: ['student midwife', 'midwifery student'],
        journeyDisplay: 'parent',
        summary: 'A student training to become a registered midwife who works with supervision appropriate to their stage of training.',
        differenceFromParent: 'A student midwife is still in training. What they may do depends on their stage of training, competence and supervision. A registered midwife remains responsible for ensuring care is appropriately supervised.',
      },
    ],
  },
  clinical_nurse_specialist: {
    parentRoleKey: 'clinical_nurse_specialist',
    variants: [
      {
        variantKey: 'cancer_cns',
        title: 'Cancer Clinical Nurse Specialist',
        plainEnglishTitle: 'Cancer Clinical Nurse Specialist (Cancer CNS)',
        aliases: ['cancer CNS'],
        searchTerms: ['cancer clinical nurse specialist', 'cancer cns'],
        journeyDisplay: 'variant_label',
        summary: 'A Clinical Nurse Specialist with expert knowledge of cancer care who may be a key contact during diagnosis, treatment and follow-up.',
        differenceFromParent: 'A Cancer CNS is a Clinical Nurse Specialist whose expertise is focused on cancer care. The core CNS role is similar, but their specialist knowledge, pathway and patient group are cancer-specific.',
      },
      {
        variantKey: 'breast_cancer_cns',
        title: 'Breast Cancer Clinical Nurse Specialist',
        plainEnglishTitle: 'Breast Cancer CNS',
        aliases: ['breast CNS'],
        searchTerms: ['breast cancer cns', 'breast cns', 'breast cancer clinical nurse specialist'],
        journeyDisplay: 'variant_label',
        summary: 'A Cancer CNS specialising in breast cancer care and support.',
        differenceFromParent: 'A Breast Cancer CNS is a Clinical Nurse Specialist whose specialist expertise is focused on breast cancer pathways, treatment and support.',
      },
      {
        variantKey: 'cardiac_cns',
        title: 'Cardiac Clinical Nurse Specialist',
        plainEnglishTitle: 'Cardiac CNS',
        aliases: ['cardiology CNS'],
        searchTerms: ['cardiac cns', 'cardiology cns', 'cardiac clinical nurse specialist'],
        journeyDisplay: 'variant_label',
        summary: 'A Clinical Nurse Specialist with expert knowledge of heart conditions and cardiac care.',
        differenceFromParent: 'A Cardiac CNS is a Clinical Nurse Specialist whose expertise is focused on cardiac conditions, treatment and ongoing support.',
      },
      {
        variantKey: 'diabetes_cns',
        title: 'Diabetes Clinical Nurse Specialist',
        plainEnglishTitle: 'Diabetes CNS',
        aliases: ['diabetes specialist nurse'],
        searchTerms: ['diabetes cns', 'diabetes clinical nurse specialist', 'diabetes specialist nurse'],
        journeyDisplay: 'variant_label',
        summary: 'A Clinical Nurse Specialist with expert knowledge of diabetes care.',
        differenceFromParent: 'A Diabetes CNS is a Clinical Nurse Specialist whose expertise is focused on diabetes, including treatment, education and support with complex diabetes care.',
      },
      {
        variantKey: 'respiratory_cns',
        title: 'Respiratory Clinical Nurse Specialist',
        plainEnglishTitle: 'Respiratory CNS',
        aliases: ['respiratory specialist nurse'],
        searchTerms: ['respiratory cns', 'respiratory clinical nurse specialist', 'respiratory specialist nurse'],
        journeyDisplay: 'variant_label',
        summary: 'A Clinical Nurse Specialist with expert knowledge of respiratory conditions and care.',
        differenceFromParent: 'A Respiratory CNS is a Clinical Nurse Specialist whose expertise is focused on lung and breathing conditions and their treatment.',
      },
      {
        variantKey: 'neurology_cns',
        title: 'Neurology Clinical Nurse Specialist',
        plainEnglishTitle: 'Neurology CNS',
        aliases: ['neurology specialist nurse'],
        searchTerms: ['neurology cns', 'neurology clinical nurse specialist', 'neurology specialist nurse'],
        journeyDisplay: 'variant_label',
        summary: 'A Clinical Nurse Specialist with expert knowledge of neurological conditions and care.',
        differenceFromParent: 'A Neurology CNS is a Clinical Nurse Specialist whose expertise is focused on neurological conditions, treatment and support.',
      },
      {
        variantKey: 'palliative_care_cns',
        title: 'Palliative Care Clinical Nurse Specialist',
        plainEnglishTitle: 'Palliative Care CNS',
        aliases: ['palliative care specialist nurse'],
        searchTerms: ['palliative care cns', 'palliative care clinical nurse specialist', 'palliative care specialist nurse'],
        journeyDisplay: 'variant_label',
        summary: 'A Clinical Nurse Specialist with expert knowledge of symptom control, supportive care and palliative care.',
        differenceFromParent: 'A Palliative Care CNS is a Clinical Nurse Specialist whose expertise is focused on symptom control, supportive care and quality of life for people with serious illness.',
      },
    ],
  },
  surgeon: {
    parentRoleKey: 'surgeon',
    variants: [
      {
        variantKey: 'neurosurgeon',
        title: 'Neurosurgeon',
        plainEnglishTitle: 'Neurosurgeon',
        aliases: ['neurosurgical surgeon'],
        searchTerms: ['neurosurgeon', 'neurosurgery surgeon'],
        journeyDisplay: 'variant_label',
        summary: 'A surgeon specialising in operations involving the brain, spine and nervous system.',
        differenceFromParent: 'A neurosurgeon is a surgeon whose specialist training is focused on operations involving the brain, spine and nervous system.',
      },
      {
        variantKey: 'cardiac_surgeon',
        title: 'Cardiac Surgeon',
        plainEnglishTitle: 'Cardiac Surgeon',
        aliases: ['heart surgeon', 'cardiothoracic surgeon'],
        searchTerms: ['cardiac surgeon', 'heart surgeon', 'cardiothoracic surgeon'],
        journeyDisplay: 'variant_label',
        summary: 'A surgeon specialising in operations on the heart and, depending on the service, other structures in the chest.',
        differenceFromParent: 'A cardiac surgeon is a surgeon whose specialist training is focused on operations on the heart. Some services use the broader title cardiothoracic surgeon.',
      },
      {
        variantKey: 'general_surgeon',
        title: 'General Surgeon',
        plainEnglishTitle: 'General Surgeon',
        aliases: [],
        searchTerms: ['general surgeon'],
        journeyDisplay: 'variant_label',
        summary: 'A surgeon who performs a broad range of operations, often involving the abdomen and digestive system.',
        differenceFromParent: 'A general surgeon is a surgeon whose specialist practice commonly includes abdominal, gastrointestinal and other general surgical conditions.',
      },
      {
        variantKey: 'orthopaedic_surgeon',
        title: 'Orthopaedic Surgeon',
        plainEnglishTitle: 'Orthopaedic Surgeon',
        aliases: ['orthopedic surgeon'],
        searchTerms: ['orthopaedic surgeon', 'orthopedic surgeon'],
        journeyDisplay: 'variant_label',
        summary: 'A surgeon specialising in bones, joints, muscles and related injuries or conditions.',
        differenceFromParent: 'An orthopaedic surgeon is a surgeon whose specialist training is focused on the musculoskeletal system, including bones and joints.',
      },
      {
        variantKey: 'vascular_surgeon',
        title: 'Vascular Surgeon',
        plainEnglishTitle: 'Vascular Surgeon',
        aliases: [],
        searchTerms: ['vascular surgeon'],
        journeyDisplay: 'variant_label',
        summary: 'A surgeon specialising in blood vessels and circulation outside the heart.',
        differenceFromParent: 'A vascular surgeon is a surgeon whose specialist training is focused on arteries, veins and circulation outside the heart.',
      },
      {
        variantKey: 'ent_surgeon',
        title: 'ENT Surgeon',
        plainEnglishTitle: 'Ear, Nose and Throat (ENT) Surgeon',
        aliases: ['otolaryngologist', 'ear nose and throat surgeon'],
        searchTerms: ['ent surgeon', 'ear nose and throat surgeon', 'otolaryngologist'],
        journeyDisplay: 'variant_label',
        summary: 'A surgeon specialising in conditions affecting the ear, nose, throat and related head and neck structures.',
        differenceFromParent: 'An ENT surgeon is a surgeon whose specialist training is focused on the ear, nose, throat and related head and neck structures.',
      },
      {
        variantKey: 'urological_surgeon',
        title: 'Urological Surgeon',
        plainEnglishTitle: 'Urological Surgeon',
        aliases: ['urologist'],
        searchTerms: ['urological surgeon', 'urologist'],
        journeyDisplay: 'variant_label',
        summary: 'A surgeon specialising in the urinary system and male reproductive system.',
        differenceFromParent: 'A urological surgeon is a surgeon whose specialist training is focused on the urinary system and male reproductive system.',
      },
      {
        variantKey: 'gynaecological_surgeon',
        title: 'Gynaecological Surgeon',
        plainEnglishTitle: 'Gynaecological Surgeon',
        aliases: ['gynecological surgeon'],
        searchTerms: ['gynaecological surgeon', 'gynecological surgeon'],
        journeyDisplay: 'variant_label',
        summary: 'A surgeon specialising in operations involving the female reproductive system.',
        differenceFromParent: 'A gynaecological surgeon is a surgeon whose specialist surgical practice is focused on the female reproductive system.',
      },
      {
        variantKey: 'plastic_surgeon',
        title: 'Plastic Surgeon',
        plainEnglishTitle: 'Plastic Surgeon',
        aliases: ['reconstructive surgeon'],
        searchTerms: ['plastic surgeon', 'reconstructive surgeon'],
        journeyDisplay: 'variant_label',
        summary: 'A surgeon specialising in reconstructive and plastic surgery.',
        differenceFromParent: 'A plastic surgeon is a surgeon whose specialist training includes reconstructive surgery and, in some services, cosmetic surgery.',
      },
      {
        variantKey: 'maxillofacial_surgeon',
        title: 'Oral and Maxillofacial Surgeon',
        plainEnglishTitle: 'Oral and Maxillofacial Surgeon',
        aliases: ['maxillofacial surgeon', 'omfs surgeon'],
        searchTerms: ['oral and maxillofacial surgeon', 'maxillofacial surgeon', 'omfs surgeon'],
        journeyDisplay: 'variant_label',
        summary: 'A surgeon specialising in the mouth, jaws, face and related structures.',
        differenceFromParent: 'An oral and maxillofacial surgeon is a surgeon whose specialist practice focuses on the mouth, jaws, face and related structures.',
      },
      {
        variantKey: 'ophthalmic_surgeon',
        title: 'Ophthalmic Surgeon',
        plainEnglishTitle: 'Ophthalmic Surgeon',
        aliases: ['eye surgeon', 'ophthalmologist'],
        searchTerms: ['ophthalmic surgeon', 'eye surgeon', 'ophthalmologist'],
        journeyDisplay: 'variant_label',
        summary: 'A surgeon specialising in operations and treatment involving the eyes.',
        differenceFromParent: 'An ophthalmic surgeon is a surgeon whose specialist training is focused on the eyes and visual system.',
      },
      {
        variantKey: 'paediatric_surgeon',
        title: 'Paediatric Surgeon',
        plainEnglishTitle: 'Paediatric Surgeon',
        aliases: ['pediatric surgeon'],
        searchTerms: ['paediatric surgeon', 'pediatric surgeon'],
        journeyDisplay: 'variant_label',
        summary: 'A surgeon specialising in operations for babies, children and young people.',
        differenceFromParent: 'A paediatric surgeon is a surgeon whose specialist training is focused on surgical care for babies, children and young people.',
      },
    ],
  },
};

export const ROLE_VARIANTS = Object.values(ROLE_FAMILIES).flatMap((family) =>
  family.variants.map((variant) => ({ ...variant, parentRoleKey: family.parentRoleKey })),
);
